import { is } from "@callcenter/helpers"

import { blocks } from "@callcenter-frontend/web"
import { adminBlocks } from "@callcenter-frontend/web-admin"
import type { AppsCollection } from "@callcenter-frontend/app-initializer"
import { AgentWorkspaceHeader, subscribeAgentCommands } from "@callcenter-frontend/header"

import { reportMfRemoteFailure } from "./flow-builder/flow-builder.helpers"

export const APPS: AppsCollection = {
  report: blocks.Report,
  senderIds: blocks.SendersID,
  "acl-list": blocks.SecurityGroups,
  "users-list": blocks.Users,
  "flows-list_old": blocks.Flows,
  "flows-list": async () => {
    try {
      // eslint-disable-next-line @nx/enforce-module-boundaries
      const remote = await import("@callcenter-remote/flows/flow-list")

      if (is.function(remote.init)) {
        return {
          init: (el: HTMLElement) => {
            subscribeAgentCommands()
            remote.init(el)
          }
        }
      }
    } catch (error) {
      reportMfRemoteFailure("@callcenter-remote/flows/flow-list", error)
    }

    return blocks.Flows()
  },
  "agent-workspace": async () => {
    try {
      // eslint-disable-next-line @nx/enforce-module-boundaries
      const remote = await import("@callcenter-remote/agent-workspace/agent-workspace")

      if (is.function(remote.init)) {
        const remoteInit = remote.init

        return { init: (el: HTMLElement) => remoteInit(el, { Header: AgentWorkspaceHeader }) }
      }
    } catch (error) {
      reportMfRemoteFailure("@callcenter-remote/agent-workspace/agent-workspace", error)
    }

    // eslint-disable-next-line @nx/enforce-module-boundaries
    return import(/* webpackChunkName: "OmnichannelApp" */ "@callcenter-frontend/omnichannel")
  },
  "flow-builder": () => import(/* webpackChunkName: "FlowBuilderHost" */ "./flow-builder/flow-builder"),
  "flow-builder_old": () => import(/* webpackChunkName: "FlowBuilderHost" */ "./flow-builder/flow-builder"),
  acl: blocks.SecurityGroups,
  lines: blocks.Lines,
  teams: blocks.Teams,
  billing: blocks.Billing,
  blockingNumber: blocks.BlockingNumbers,
  "broadcast-messages": blocks.BroadcastMessages,
  "mynumbers-container": blocks.MyNumbers,
  messageTemplates: blocks.MessageTemplates,
  keyword_groups: blocks.KeywordGroups,
  "media-files": blocks.MediaList,
  wrapUp: blocks.WrapUp,
  statuses: blocks.UnavailableCodes,
  "risk-checks": blocks.CheckRisk,
  metrics: blocks.Dashboards,
  "cid-groups": blocks.CidGroups,
  "cid-group": blocks.CidGroups,
  campaigns: blocks.Campaigns,
  integrations: blocks.CrmIntegration,
  skills: blocks.Skills,
  skill: blocks.Skills,
  delete_recordings: blocks.DeleteRecordings,
  cdr: blocks.CdrExtended,
  filters: blocks.Cdr,
  sa_users: adminBlocks.Users,
  sr_topics: blocks.Topics,
  "sa_media-servers": adminBlocks.MediaServers,
  "sa_media-servers-new": adminBlocks.MediaServersNew,
  "sa_kamailio-gateways-new": adminBlocks.KamailioGatewaysNew,
  audits: adminBlocks.Audits,
  "sa_dialer-stats": adminBlocks.DialerStats,
  "sa_dialer-stats-show": adminBlocks.DialerStatsShow,
  "callcenter-settings": blocks.Settings,
  "service-notifications": adminBlocks.ServiceNotifications,
  "sa_credit-cards-log": adminBlocks.CreditCardLogs,
  "allowed-ips": blocks.AllowedIps,
  "two-factor-auth-setup": blocks.TwoFactorAuthSetup,
  "number-validator": blocks.NumberValidator,
  dncr: blocks.Dncr,
  "prefix-overrides": blocks.PrefixOverrides,
  "call-suppression": blocks.CallSuppression,
  "webhooks-list": blocks.Webhooks,
  "digital-channels": blocks.DigitalChannels,
  "virtual-users": blocks.AiAgents,
  "knowledge-bases": blocks.KnowledgeBases,
  voiceProviders: blocks.VoiceProviders,
  "api-keys": blocks.StaticApiKeys,
  interaction_context_keys: blocks.ContextMapping,
  sa_callcenters: adminBlocks.Callcenters,
  sa_status_logs: adminBlocks.StatusLogs,
  sa_incoming_numbers_prices: adminBlocks.IncomingNumbersPrices,
  sa_prefix_price_overrides: adminBlocks.PrefixPriceOverrides,
  sa_transactions: adminBlocks.Transactions,
  sa_call_price_overrides: adminBlocks.CallPriceOverrides,
  "inbound-allowed-ips": blocks.InboundAllowedIps
}

const ADMIN_BLOCK_VALUES = new Set<unknown>(Object.values(adminBlocks))

export const ADMIN_APP_KEYS: ReadonlyArray<string> = Object.entries(APPS)
  .filter(([, value]) => ADMIN_BLOCK_VALUES.has(value))
  .map(([key]) => key)
